import React, { useEffect, useState, useCallback } from 'react';
import { Bell, Check, UserPlus, FileText, Mail, FileDown, CreditCard, MessageSquare, Bot } from 'lucide-react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'token';
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ICONS = {
  user_registered: UserPlus,
  application_submitted: FileText,
  lead_captured: Mail,
  programme_downloaded: FileDown,
  payment_received: CreditCard,
  contact_message: MessageSquare,
  chat_handover: Bot,
};

const ICON_COLOR = {
  user_registered: 'text-emerald-600 bg-emerald-100',
  application_submitted: 'text-amber-600 bg-amber-100',
  lead_captured: 'text-sky-600 bg-sky-100',
  programme_downloaded: 'text-violet-600 bg-violet-100',
  payment_received: 'text-rose-600 bg-rose-100',
  contact_message: 'text-indigo-600 bg-indigo-100',
  chat_handover: 'text-teal-600 bg-teal-100',
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const now = new Date();
  const then = new Date(iso);
  const diff = (now - then) / 1000;
  if (diff < 60) return "à l'instant";
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
  return then.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
};

/**
 * Admin bell — polls /api/admin/notifications every 30 s, renders a
 * compact dropdown listing recent events with quick deep-link navigation.
 */
export const AdminNotificationsBell = ({ variant = 'light' }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/notifications?limit=20`, { headers: authHeaders() });
      setItems(res.data.items || []);
      setUnread(res.data.unread_count || 0);
    } catch (_) { /* silently ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/api/admin/notifications?limit=20`, { headers: authHeaders() });
        if (cancelled) return;
        setItems(res.data.items || []);
        setUnread(res.data.unread_count || 0);
      } catch (_) { /* silently ignore */ }
    })();
    const id = setInterval(fetchNotifs, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [fetchNotifs]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/api/admin/notifications/${id}/read`, {}, { headers: authHeaders() });
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      setUnread((u) => Math.max(0, u - 1));
    } catch (_) { /* silently ignore */ }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/api/admin/notifications/mark-all-read`, {}, { headers: authHeaders() });
      setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnread(0);
    } catch (_) { /* silently ignore */ }
  };

  const handleClick = async (n) => {
    if (!n.is_read) await markRead(n.id);
    if (n.href) {
      setOpen(false);
      // /admin?section=users — re-use current pathname's lang prefix
      const langMatch = location.pathname.match(/^\/(fr|en|es|de|it|pt|ja|ko)\//);
      const lang = langMatch ? langMatch[1] : 'fr';
      const path = n.href.startsWith('/') ? n.href : `/${n.href}`;
      navigate(`/${lang}${path}`);
    }
  };

  const isDark = variant === 'dark';

  return (
    <div className="relative" data-testid="admin-notifications-bell">
      <button
        onClick={() => setOpen(!open)}
        className={`relative w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
          isDark
            ? 'bg-white/8 hover:bg-white/15 border border-white/15'
            : 'bg-white border border-[#1F2A4A]/15 hover:border-[#C9B896]'
        }`}
        data-testid="bell-button"
        title="Notifications"
      >
        <Bell className={`w-4 h-4 ${isDark ? 'text-white/85' : 'text-[#1F2A4A]'}`} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C9B896] text-[#1F2A4A] text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-[55]" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 mt-2 w-[380px] max-w-[92vw] bg-white border border-[#1F2A4A]/12 rounded-2xl shadow-xl z-[60] overflow-hidden" data-testid="bell-dropdown">
            <header className="flex items-center justify-between px-5 py-3 border-b border-[#1F2A4A]/8 bg-[#FAF7F0]/60">
              <div>
                <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#C9B896]">Activité</p>
                <p className="text-sm font-semibold text-[#1F2A4A]">{unread > 0 ? `${unread} non lue${unread > 1 ? 's' : ''}` : 'Tout est lu'}</p>
              </div>
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#1F2A4A] hover:text-[#C9B896] underline" data-testid="bell-mark-all-read">
                  Tout marquer comme lu
                </button>
              )}
            </header>
            <ul className="max-h-[480px] overflow-y-auto">
              {items.length === 0 && (
                <li className="px-5 py-10 text-center text-sm text-[#4A5568]">
                  <Bell className="w-7 h-7 mx-auto text-[#C9B896] mb-2 opacity-60" />
                  Aucune activité pour le moment.
                </li>
              )}
              {items.map((n) => {
                const Icon = ICONS[n.type] || Bell;
                const colorCls = ICON_COLOR[n.type] || 'text-[#1F2A4A] bg-[#FAF7F0]';
                return (
                  <li
                    key={n.id}
                    className={`px-5 py-3 border-b border-[#1F2A4A]/6 last:border-b-0 flex items-start gap-3 cursor-pointer hover:bg-[#FAF7F0]/60 transition-colors ${
                      n.is_read ? 'opacity-65' : ''
                    }`}
                    onClick={() => handleClick(n)}
                    data-testid={`notif-${n.id}`}
                  >
                    <div className={`mt-0.5 w-9 h-9 rounded-full ${colorCls} flex items-center justify-center shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1F2A4A] truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-[#4A5568] truncate mt-0.5">{n.body}</p>}
                      <p className="text-[10px] uppercase tracking-wider text-[#C9B896] mt-1">{formatRelative(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <span className="mt-2 w-2 h-2 rounded-full bg-[#C9B896] shrink-0" aria-hidden="true" />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminNotificationsBell;
